import React, { useState, useEffect } from "react";
import {
  Toolbar,
  Typography,
  IconButton,
  Button,
  Grid,
  Box,
  Chip,
  Paper,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import HexaLoader from "../Components/loader/Loader";
import {
  CloseOutlined,
  FindInPage as FindInPageIcon,
  ManageSearch as ManageSearchIcon,
  AddTaskOutlined,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import authService from "../services/authService";
import { getAccountsByPlatform } from "../services/socialMediaAccountsService";
import { getPostHistoryall } from "../services/postHistoryService";
import Header from "../Components/header/Header";
import PostCard from "../Components/postCard/PostCard";
import { apiGet, apiPost } from "../services/api";
import CookieUploader from "../Components/cookieManagement/cookieUploader";
import Sidebar from "../Components/sideBar/sideBar";

const drawerWidth = 260;

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tweets, setTweets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [routeLoading, setRouteLoading] = useState(false);
  const [keywords, setKeywords] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [filteredTweets, setFilteredTweets] = useState([]);
  const [selectedKeywordForPrompts, setSelectedKeywordForPrompts] =
    useState(null);
  const [keywordPromptsDialogOpen, setKeywordPromptsDialogOpen] =
    useState(false);
  const [dataSource, setDataSource] = useState("cache");
  const [postedTweets, setPostedTweets] = useState([]);
  const [availablePrompts, setAvailablePrompts] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState("");
  const [keywordPromptMap, setKeywordPromptMap] = useState({});
  const [keywordPromptsMap, setKeywordPromptsMap] = useState({});
  const [twitterAccounts, setTwitterAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [fetchAttempted, setFetchAttempted] = useState(false);
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [selectedTweet, setSelectedTweet] = useState(null);
  const [editedReply, setEditedReply] = useState("");
  const [isPosting, setIsPosting] = useState(false);
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);

  // --- State for MUI Snackbar ---
  const [errorPopup, setErrorPopup] = useState({ open: false, message: "" });

  // --- Helper function to show the snackbar ---
  const showErrorPopup = (message) => {
    setErrorPopup({ open: true, message: message });
  };

  const fetchTwitterAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const response = await getAccountsByPlatform("twitter");
      const accounts = response.data || [];
      setTwitterAccounts(accounts);
      const defaultAccount = accounts.find(
        (account) => account.isDefault === true
      );
      if (defaultAccount) {
        setSelectedAccount(defaultAccount.id);
      } else {
        setSelectedAccount(null);
      }
    } catch (err) {
      console.error("Failed to fetch Twitter accounts", err);
      showErrorPopup(
        "Could not fetch Twitter accounts. Please check your connection and try again."
      );
      setTwitterAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchPostHistory = async () => {
    try {
      const result = await getPostHistoryall();
      if (result.success && result.data) {
        const tweetIds = result.data.map((post) => post.tweetid);
        setPostedTweets(tweetIds);
      }
    } catch (err) {
      console.error("Failed to fetch post history:", err);
    }
  };

  useEffect(() => {
    fetchTwitterAccounts();
    fetchPostHistory();
    fetchPrompts();
    fetchKeywordPromptAssociations();
  }, []);

  const fetchKeywordPromptAssociations = async () => {
    try {
      const resp = await apiGet(`/keywords`);
      if (resp.success && resp.data) {
        const keywordData = resp.data;
        const promptMap = {};
        const keywordPromptsMap = {};

        keywordData.forEach((keyword) => {
          if (keyword.promptId) {
            promptMap[keyword.text] = keyword.promptId.toString();
          }
          if (keyword.prompts && keyword.prompts.length > 0) {
            keywordPromptsMap[keyword.text] = keyword.prompts;
          }
        });
        setKeywordPromptMap(promptMap);
        setKeywordPromptsMap(keywordPromptsMap);
      }
    } catch (error) {
      console.error("Error fetching keyword-prompt associations:", error);
    }
  };

  const fetchPrompts = async (keywordId = null) => {
    try {
      let endpoint = `/prompts`;
      if (keywordId) {
        endpoint += `?keywordId=${keywordId}`;
      }
      const resp = await apiGet(endpoint);
      if (resp.success && resp.data) {
        const dbPrompts = resp.data.map((prompt) => ({
          id: prompt.id.toString(),
          name: prompt.name,
          content: prompt.content,
          model: prompt.model,
        }));
        
        const modelToReturn = dbPrompts.length > 0 ? dbPrompts[0].model : "gemini-2.5-flash";

        if (keywordId) {
          return { prompts: dbPrompts, model: modelToReturn };
        } else {
          setAvailablePrompts(dbPrompts);
          const defaultPrompt = dbPrompts.find((p) => p.name === "Default");
          if (defaultPrompt) {
            setSelectedPromptId(defaultPrompt.id);
          }
          return { model: modelToReturn };
        }
      }
    } catch (error) {
      console.error("Error fetching prompts:", error);
      if (!keywordId) {
        setAvailablePrompts([
          {
            id: "default",
            name: "Default",
            content: `Reply smartly to this tweet:\n"{tweetContent}"\nMake it personal, friendly, and relevant.`,
            model: "meta-llama/llama-3-8b-instruct",
          },
        ]);
      }
    }
    return { prompts: [], model: "gemini-2.5-flash" };
  };


  useEffect(() => {
    if (
      location.pathname.includes("/dashboard") ||
      location.pathname.includes("/history") ||
      location.pathname.includes("/social-media-settings") ||
      location.pathname.includes("/trending-analytics") ||
      location.pathname.includes("/post-history")
    ) {
      setRouteLoading(true);
      const timer = setTimeout(() => {
        setRouteLoading(false);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  const fetchAllKeywords = async () => {
    try {
      const resp = await apiGet(`/keywords`);
      return resp.data || [];
    } catch (err) {
      console.error("Failed to fetch keywords", err);
      return [];
    }
  };

  const fetchAllPosts = async (
    accountId = selectedAccount,
    forceRefresh = false
  ) => {
    setLoading(true);
    setFetchAttempted(true);
    setDataSource("cache");
    try {
      const allKeywords = await fetchAllKeywords();
      const existingTweets = forceRefresh ? [...tweets] : [];
      const filteredKeywords = accountId
        ? allKeywords.filter((k) => String(k.accountId) === String(accountId))
        : allKeywords;
      setKeywords(filteredKeywords);

      if (filteredKeywords.length === 0) {
        setTweets([]);
        setLoading(false);
        return;
      }

      let allFetchedTweets = [];
      for (const keyword of filteredKeywords) {
        const minLikes = Number(keyword.minLikes) || 0;
        const minRetweets = Number(keyword.minRetweets) || 0;
        const minFollowers = Number(keyword.minFollowers) || 0;

        let endpoint =
          `/search?keyword=${encodeURIComponent(keyword.text)}` +
          `&minLikes=${minLikes}&minRetweets=${minRetweets}&minFollowers=${minFollowers}&language=en`;

        if (accountId) endpoint += `&accountId=${accountId}`;
        if (forceRefresh) endpoint += `&forceRefresh=true&analyze=true`;

        try {
          const res = await apiGet(endpoint);
          if (res?.from) setDataSource(res.from);

          const isAnalyzeShape =
            forceRefresh &&
            res?.tweets &&
            !Array.isArray(res.tweets) &&
            (Array.isArray(res.tweets.allowed) ||
              Array.isArray(res.tweets.downranked) ||
              Array.isArray(res.tweets.blocked));

          if (isAnalyzeShape) {
            const buckets = ["allowed", "downranked", "blocked"];
            for (const bucket of buckets) {
              const arr = Array.isArray(res.tweets[bucket])
                ? res.tweets[bucket]
                : [];
              const mapped = arr.map((t) => ({
                ...t,
                id: t.id || t.tweet_id,
                keyword: keyword.text,
                status: bucket,
              }));
              allFetchedTweets.push(...mapped);
            }
          } else {
            const list =
              (Array.isArray(res?.tweets) && res.tweets) ||
              (Array.isArray(res?.posts) && res.posts) ||
              (Array.isArray(res?.data) && res.data) ||
              (Array.isArray(res) && res) ||
              [];
            const keywordTweets = list.map((t) => ({
              ...t,
              id: t.id || t.tweet_id,
              reply: t.reply ?? null,
              keyword: keyword.text,
            }));
            allFetchedTweets.push(...keywordTweets);
          }
        } catch (err) {
          console.error(
            `Error fetching tweets for keyword "${keyword.text}":`,
            err
          );
          const errorMessage =
            err.response?.data?.message ||
            err.message ||
            "An unexpected error occurred.";
          showErrorPopup(errorMessage);
          break;
        }
      }

      const combinedTweets = [...allFetchedTweets, ...existingTweets];
      const dedupMap = new Map();
      for (const t of combinedTweets) {
        const key = String(t.id || t.tweet_id || "");
        if (!key) continue;
        dedupMap.set(key, { id: t.id || t.tweet_id, ...t });
      }
      const uniqueTweets = Array.from(dedupMap.values());
      setTweets(uniqueTweets);

      if (forceRefresh && uniqueTweets.length > 0) {
        setTimeout(() => {
          autoGenerateRepliesForTweets(uniqueTweets);
        }, 1000);
      }
    } catch (err) {
      console.error("An error occurred in the fetchAllPosts process:", err);
      showErrorPopup("An unexpected error occurred while fetching posts.");
    } finally {
      setLoading(false);
    }
  };

  const [didRunInitialFetch, setDidRunInitialFetch] = useState(false);
  useEffect(() => {
    if (loadingAccounts) return;

    if (didRunInitialFetch) {
      setFetchAttempted(false);
      fetchAllPosts(selectedAccount ?? null, false);
    } else {
      fetchAllPosts(selectedAccount ?? null, false);
      setDidRunInitialFetch(true);
    }
  }, [loadingAccounts, selectedAccount]);

  const handlePost = async (tweet) => {
    setSelectedTweet(tweet);
    setPostDialogOpen(true);
    if (tweet.reply) {
      setEditedReply(tweet.reply);
    } else {
      setEditedReply("Generating reply, please wait...");
      const newReply = await generateAndSaveSingleReply(tweet);
      setEditedReply(newReply);
      setTweets((prevTweets) =>
        prevTweets.map((t) =>
          t.id === tweet.id ? { ...t, reply: newReply } : t
        )
      );
    }
    setIsEditingPrompt(false);
  
    let keywordId = null;
    if (tweet.keyword) {
      const keywordObj =
        keywords.find((k) => k.text === tweet.keyword) ||
        (await fetchAllKeywords()).find((k) => k.text === tweet.keyword);
      keywordId = keywordObj ? keywordObj.id : null;
    }
  
    const { prompts: keywordSpecificPrompts, model: modelToUse } = await fetchPrompts(keywordId);
  
    let aiGeneratedPrompt = null;
    try {
      const analysisResponse = await apiPost(`/analyze-and-generate-prompt`, {
        tweetContent: tweet.text,
        keywordId: keywordId,
        model: modelToUse || "gemini-2.5-flash",
      });
      if (analysisResponse.data.success) {
        aiGeneratedPrompt = analysisResponse.data.data.aiGeneratedPrompt;
      }
    } catch (error) {
      console.error("Error generating AI-enhanced prompt:", error);
      showErrorPopup(`Failed to Generate reply from AI services`);
    }
    
    let finalPrompts = [...keywordSpecificPrompts];
    if (aiGeneratedPrompt) {
      finalPrompts = [aiGeneratedPrompt, ...finalPrompts];
    }
  
    if (finalPrompts.length > 0) {
      setAvailablePrompts(finalPrompts);
      setSelectedPromptId(finalPrompts[0].id);
    } else {
const { prompts: allPrompts = [] } = (await fetchPrompts()) || {};
      let fallbackPrompts = aiGeneratedPrompt
        ? [aiGeneratedPrompt, ...allPrompts]
        : [...allPrompts];
      setAvailablePrompts(fallbackPrompts);
      const defaultPrompt =
        fallbackPrompts.find((p) => p.name === "Default") ||
        fallbackPrompts[0];
      if (defaultPrompt) {
        setSelectedPromptId(defaultPrompt.id);
      }
    }
  };

  const analyzeTweetContent = async (tweet) => {
    try {
      const keywordObj = keywords.find((k) => k.text === tweet.keyword);
      const keywordId = keywordObj ? keywordObj.id : null;

      try {
        const { model: modelForAnalysis } = await fetchPrompts(keywordId);
        const analysisResponse = await apiPost(
          `/analyze-and-generate-prompt`,
          {
            tweetContent: tweet.text,
            keywordId: keywordId,
            model: modelForAnalysis
          }
        );
        if (analysisResponse.success) {
          const aiGeneratedPrompt = analysisResponse.data.aiGeneratedPrompt;
          const analysisResult = analysisResponse.data.analysisResult;
          setTweets((prevTweets) =>
            prevTweets.map((t) =>
              t.id === tweet.id ? { ...t, analysisResult: analysisResult } : t
            )
          );
          return {
            prompt: aiGeneratedPrompt,
            promptId: aiGeneratedPrompt.id,
            model: aiGeneratedPrompt.model || "meta-llama/llama-3-8b-instruct",
          };
        }
      } catch (error) {
        console.error("Error generating AI-enhanced prompt:", error);
      }

      if (tweet.keyword && keywordPromptsMap[tweet.keyword]) {
        const keywordPromptsList = keywordPromptsMap[tweet.keyword];
        if (keywordPromptsList.length > 0) {
          const bestPrompt = keywordPromptsList[0];
          return {
            prompt: bestPrompt,
            promptId: bestPrompt.id.toString(),
            model: bestPrompt.model || "meta-llama/llama-3-8b-instruct",
          };
        }
      }

      if (availablePrompts.length > 0) {
        const defaultPrompt =
          availablePrompts.find((p) => p.name === "Default") ||
          availablePrompts[0];
        return {
          prompt: defaultPrompt,
          promptId: defaultPrompt.id,
          model: defaultPrompt.model || "meta-llama/llama-3-8b-instruct",
        };
      }

      return {
        prompt: {
          id: "default",
          name: "Default",
          content: `Reply smartly to this tweet:\n"{tweetContent}"\nMake it personal, friendly, and relevant.`,
          model: "meta-llama/llama-3-8b-instruct",
        },
        promptId: "default",
        model: "meta-llama/llama-3-8b-instruct",
      };
    } catch (error) {
      console.error(`Error analyzing tweet content for tweet ${tweet.id}:`, error);
      return {
        prompt: {
          id: "fallback",
          name: "Fallback",
          content: `Reply smartly to this tweet:\n"{tweetContent}"\nMake it personal, friendly, and relevant.`,
          model: "meta-llama/llama-3-8b-instruct",
        },
        promptId: "fallback",
        model: "meta-llama/llama-3-8b-instruct",
      };
    }
  };

  const autoGenerateRepliesForTweets = async (tweetsToProcess) => {
    const batchSize = 5;
    const tweetsNeedingReplies = tweetsToProcess.filter((tweet) => !tweet.reply);

    if (tweetsNeedingReplies.length === 0) {
      return;
    }

    for (let i = 0; i < tweetsNeedingReplies.length; i += batchSize) {
      const batch = tweetsNeedingReplies.slice(i, i + batchSize);
      await Promise.all(
        batch.map(async (tweet) => {
          try {
            // 1. Find the best prompt and model for THIS specific tweet
            const promptAnalysis = await analyzeTweetContent(tweet);
            const { prompt: bestPrompt, promptId, model } = promptAnalysis;

            if (!bestPrompt) {
              console.error("No prompt found for tweet:", tweet.id);
              return;
            }

            // (Caching logic remains the same)

            const formattedPrompt = bestPrompt.content.replace(
              /{tweetContent}/g,
              tweet.text
            );
            // 2. Use the model found in the analysis, with a fallback
            const finalModel = model || "gemini-2.5-flash";

            // 3. Pass the correct model to the /generate-reply API
            const response = await apiPost(`/generate-reply`, {
              model: finalModel,
              messages: [{ role: "user", content: formattedPrompt }],
            });

            if (response && response.reply) {
              const generatedReply = response.reply;
              setTweets((prevTweets) =>
                prevTweets.map((t) =>
                  t.id === tweet.id ? { ...t, reply: generatedReply } : t
                )
              );
              await apiPost(`/save-reply`, {
                tweetId: tweet.id,
                promptId: promptId,
                model: finalModel,
                reply: generatedReply,
              });
            }
          } catch (error) {
            console.error(`Error generating reply for tweet ${tweet.id}:`, error);
          }
        })
      );
      if (i + batchSize < tweetsNeedingReplies.length) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }
  };

  const generateAndSaveSingleReply = async (tweet) => {
    try {
      // 1. Find the best prompt and model for THIS specific tweet
      const promptAnalysis = await analyzeTweetContent(tweet);
      const { prompt: bestPrompt, promptId, model } = promptAnalysis;

      if (!bestPrompt) {
        console.error("Could not determine a prompt for the tweet.");
        return "Failed to find a suitable prompt.";
      }

      // (Caching logic remains the same)
      
      // 2. Use the model found in the analysis, with a fallback
      const finalModel = model || "gemini-2.5-flash";
      const formattedPrompt = bestPrompt.content.replace(
        /{tweetContent}/g,
        tweet.text
      );

      // 3. Pass the correct model to the /generate-reply API
      const response = await apiPost(`/generate-reply`, {
        model: finalModel,
        messages: [{ role: "user", content: formattedPrompt }],
      });

      if (response && response.reply) {
        return response.reply;
      } else {
        throw new Error("API did not return a reply.");
      }
    } catch (error) {
      console.error("Error in generateAndSaveSingleReply:", error);
      return "Sorry, an error occurred while generating the reply.";
    }
  };

  const handlePostSubmit = async () => {
    if (!selectedTweet || !editedReply) {
      return;
    }
    setIsPosting(true);
    try {
      const tweetKeyword = selectedTweet.keyword;
      const keywordObj = keywords.find((k) => k.text === tweetKeyword);
      const keywordId = keywordObj ? keywordObj.id : null;

      let selectedPrompt = availablePrompts.find(
        (p) => p.id === selectedPromptId
      );

      const model = selectedPrompt?.model || "gemini-2.5-flash";
      const promptContent = selectedPrompt?.content || "";
      const promptName = selectedPrompt?.name || "Default";
      const promptId = selectedPrompt?.id.toString() || "default";

      await apiPost(`/save-reply`, {
        tweetId: selectedTweet.id,
        promptId: promptId,
        model: model,
        reply: editedReply,
      });

      const response = await apiPost(`/reply-to-tweet`, {
        tweetId: selectedTweet.id,
        replyText: editedReply,
        selectedAccountId: selectedAccount,
        keywordId: keywordId,
        tweetText: selectedTweet.text,
        likeCount: selectedTweet.like_count,
        retweetCount: selectedTweet.retweet_count,
        model: model,
        promptContent: promptContent,
        promptName: promptName,
      });

      if (response.success) {
        alert("Reply posted successfully!");
        setPostedTweets((prev) => [...prev, selectedTweet.id]);
        setPostDialogOpen(false);
      } else {
        alert("Failed to post reply: " + (response.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Error posting reply:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An unknown error occurred.";
      showErrorPopup(`Failed to post reply: ${errorMessage}`);
    } finally {
      setIsPosting(false);
    }
  };

  useEffect(() => {
    const twitterToken = new URLSearchParams(window.location.search).get(
      "accessToken"
    );
    if (twitterToken) {
      localStorage.setItem("twitter_access_token", twitterToken);
      const convertToken = async () => {
        try {
          const response = await authService.convertTwitterToken(twitterToken);
          if (response.success) {
            localStorage.setItem("token", response.token);
            if (response.user) {
              localStorage.setItem("user", JSON.stringify(response.user));
            }
          }
        } catch (error) {
          console.error("Error converting Twitter token:", error);
        }
        navigate("/dashboard", { replace: true });
      };
      convertToken();
    }
  }, [navigate]);

  const handleKeywordToggle = (keywordText) => {
    setSelectedKeywords((prev) => {
      const isSelected = prev.includes(keywordText);
      return isSelected
        ? prev.filter((k) => k !== keywordText)
        : [...prev, keywordText];
    });
  };

  useEffect(() => {
    let filtered = tweets;
    if (selectedKeywords.length > 0) {
      filtered = tweets.filter((tweet) =>
        selectedKeywords.some(
          (keyword) =>
            tweet.keyword &&
            tweet.keyword.toLowerCase() === keyword.toLowerCase()
        )
      );
    }
    setFilteredTweets(filtered);
  }, [tweets, selectedKeywords]);

  return (
    <Box sx={{ display: "flex" }}>
      <Snackbar
        open={errorPopup.open}
        autoHideDuration={6000}
        onClose={() => setErrorPopup({ ...errorPopup, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorPopup({ ...errorPopup, open: false })}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {errorPopup.message}
        </Alert>
      </Snackbar>

      {/* Keyword Prompts Dialog */}
      <Dialog
        open={keywordPromptsDialogOpen}
        onClose={() => setKeywordPromptsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Prompts for "{selectedKeywordForPrompts?.text}"
          <IconButton
            aria-label="close"
            onClick={() => setKeywordPromptsDialogOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseOutlined />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedKeywordForPrompts &&
          keywordPromptsMap[selectedKeywordForPrompts.text] ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {keywordPromptsMap[selectedKeywordForPrompts.text].map(
                (prompt, index) => (
                  <Paper key={index} elevation={1} sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {prompt.name} {prompt.is_default && "(Default)"}
                      </Typography>
                      <Chip label={prompt.model} size="small" />
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {prompt.content}
                    </Typography>
                  </Paper>
                )
              )}
            </Box>
          ) : (
            <Typography>No prompts associated with this keyword.</Typography>

          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKeywordPromptsDialogOpen(false)}>
            Close
          </Button>
          <Button
            onClick={() => {
              if (selectedKeywordForPrompts) {
                navigate("/social-media-settings");
              }
              setKeywordPromptsDialogOpen(false);
            }}
            variant="contained"
          >
            Manage Prompts
          </Button>
        </DialogActions>
      </Dialog>

      {/* Post Reply Dialog */}
      <Dialog
        open={postDialogOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          setPostDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Post Reply</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" gutterBottom>
            Original Post Content
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, p: 2, bgcolor: "#f5f5f5" }}>
            {selectedTweet?.text || "No tweet content available."}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            Auto-Generated Reply
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={editedReply}
            onChange={(e) => setEditedReply(e.target.value)}
            placeholder="Compose a professional and concise response..."
            inputProps={{ maxLength: 220 }}
            helperText={`${editedReply.length}/220 characters ${
              editedReply.length > 220 ? "(limit exceeded)" : ""
            }`}
            error={editedReply.length > 220}
          />
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            <strong>Select Account:</strong>
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            label="Twitter Account"
            value={selectedAccount || ""}
            onChange={(e) => {
              setSelectedAccount(e.target.value === "" ? null : e.target.value);
            }}
            disabled={loadingAccounts}
          >
            <MenuItem value="">All Accounts</MenuItem>
            {Array.isArray(twitterAccounts) &&
              twitterAccounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.accountName}
                </MenuItem>
              ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handlePostSubmit}
            variant="contained"
            startIcon={isPosting ? null : <AddTaskOutlined />}
            disabled={
              isPosting || editedReply.length > 220 || !editedReply.trim()
            }
          >
            {isPosting ? "Posting..." : "Post Reply"}
          </Button>
        </DialogActions>
      </Dialog>

      <Sidebar
        drawerWidth={drawerWidth}
        onNavigate={(path) => {
          setRouteLoading(true);
          navigate(path);
          setTimeout(() => setRouteLoading(false), 800);
        }}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "#f9f9f9",
          minHeight: "100vh",
          position: "relative",
        }}
      >
        <Header />
        <Toolbar />
        {routeLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 1000,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <HexaLoader />
              <Typography
                variant="h6"
                sx={{ mt: 2, color: "#4D99A3", fontWeight: 600 }}
              >
                Loading...
              </Typography>
            </Box>
          </Box>
        )}
        <Box sx={{ p: 3 }}>
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", md: "center" },
                mb: 4,
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: "#4896a1" }}>
                  Twitter Manager
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  A central hub to post, track, and analyze tweets efficiently
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, background: "#e5efee" }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <Box sx={{ bgcolor: "#4896A0", color: "white", width: 40, height: 40, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <FindInPageIcon />
                    </Box>
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Search for Posts
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                        Click the Fetch New button to search for social media posts matching your keywords.
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => fetchAllPosts(selectedAccount, true)}
                        disabled={loading || !selectedAccount}
                        sx={{ backgroundColor: "#4D99A3", color: "white" }}
                      >
                        {loading ? "Fetching..." : "Fetch New"}
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, background: "#e5efee" }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <Box sx={{ bgcolor: "#4896A0", color: "white", width: 40, height: 40, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <ManageSearchIcon />
                    </Box>
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Manage Keywords
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                        Add or edit keywords to customize your social media search results.
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => navigate("/social-media-settings")}
                        sx={{ backgroundColor: "#4D99A3", color: "white" }}
                      >
                        Manage Keywords
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
            <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Your Keywords & Posts
                </Typography>
                <Box sx={{ minWidth: 200 }}>
                  <TextField
                    select
                    size="small"
                    label="Twitter Account"
                    value={selectedAccount || ""}
                    onChange={(e) => setSelectedAccount(e.target.value === "" ? null : e.target.value)}
                    disabled={loadingAccounts}
                    fullWidth
                  >
                    <MenuItem value="">All Accounts</MenuItem>
                    {Array.isArray(twitterAccounts) &&
                      twitterAccounts.map((account) => (
                        <MenuItem key={account.id} value={account.id}>
                          {account.accountName}
                        </MenuItem>
                      ))}
                  </TextField>
                </Box>
              </Box>
              {keywords.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {keywords.map((keyword) => {
                    const isSelected = selectedKeywords.includes(keyword.text);
                    const hasPrompts =
                      keywordPromptsMap[keyword.text] &&
                      keywordPromptsMap[keyword.text].length > 0;
                    return (
                      <Chip
                        key={keyword.id}
                        label={keyword.text}
                        onClick={() => handleKeywordToggle(keyword.text)}
                        onDelete={
                          hasPrompts
                            ? () => {
                                setSelectedKeywordForPrompts(keyword);
                                setKeywordPromptsDialogOpen(true);
                              }
                            : undefined
                        }
                        deleteIcon={
                          hasPrompts ? (
                            <Tooltip title="View prompts">
                              <ManageSearchIcon />
                            </Tooltip>
                          ) : undefined
                        }
                        color={isSelected ? "primary" : "default"}
                        sx={{
                          backgroundColor: isSelected ? "#4896a1" : "#ffffff",
                          color: isSelected ? "#ffffff" : "#4896a1",
                        }}
                      />
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No keywords configured for this account.
                </Typography>
              )}
            </Paper>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <HexaLoader />
              </Box>
            ) : filteredTweets.length > 0 ? (
              <Grid container spacing={3}>
                {filteredTweets.map((tweet) => (
                  <Grid item xs={12} sm={6} lg={4} key={tweet.id}>
                    <PostCard
                      tweet={tweet}
                      postedTweets={postedTweets}
                      dataSource={dataSource}
                      handlePost={handlePost}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : fetchAttempted ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No posts found for this account.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try fetching new posts or adding keywords in the settings.
                </Typography>
              </Box>
            ) : null}
          </>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;